import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { BloomMenuTemplate } from '@jane/business-admin/types';
import { janeApiV2 } from '@jane/shared/data-access';

import { useCurrentBloomMenuId } from './bloomMenus';

const bloomMenuTemplatesUrl = (menuId: number) =>
  `/managers/bloom_menus/${menuId}/templates`;

const bloomMenuTemplateUrl = (menuId: number) =>
  `/managers/bloom_menus/${menuId}/templates/footer`;

const bloomMenuTemplateQueryKey = (menuId: number) => [
  'bloom_menus',
  menuId,
  'templates',
];

const fetchBloomMenuTemplate = async (
  menuId: number
): Promise<BloomMenuTemplate | null> => {
  const footerUrl = bloomMenuTemplateUrl(menuId);

  try {
    const data = await janeApiV2.get<{
      bloom_menu_template: BloomMenuTemplate;
    }>(footerUrl);
    return data['bloom_menu_template'];
  } catch {
    return null;
  }
};

export const useFetchBloomMenuTemplate = () => {
  const menuId = useCurrentBloomMenuId();

  return useQuery<BloomMenuTemplate | null>({
    queryFn: () => fetchBloomMenuTemplate(menuId),
    queryKey: bloomMenuTemplateQueryKey(menuId),
    throwOnError: true,
  });
};

export interface UpsertBloomMenuTemplateArgs extends Record<string, unknown> {
  source_content_selector: string;
  source_url: string;
  template_type: string;
}

const upsertBloomMenuTemplate = async (
  updates: UpsertBloomMenuTemplateArgs,
  menuId: number
) => await janeApiV2.put(bloomMenuTemplatesUrl(menuId), updates);

interface UseUpsertBloomMenuTemplateArgs {
  /** Essentially required, but made optional to allow for react-query / react-router providing an undefined param. */
  menuId?: number;
}

export const useUpsertBloomMenuTemplate = ({
  menuId = -1,
}: UseUpsertBloomMenuTemplateArgs) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpsertBloomMenuTemplateArgs) =>
      await upsertBloomMenuTemplate(data, menuId),
    onError: console.error,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: bloomMenuTemplateQueryKey(menuId),
      });
    },
  });
};
